/*!
 * MakNorte — Wishlist + Recently Viewed
 * Mantém estado em memória e usa localStorage somente com permissão de preferências.
 */
(function(){
  const WISHLIST_KEY = 'mak_wishlist';
  const WISHLIST_HANDLES_KEY = 'mak_wishlist_handles';
  const VIEWED_KEY = 'mak_viewed';
  const MAX_NEW_WISHLIST = 24;
  const WISHLIST_PAGE_SIZE = 24;
  const WISHLIST_REQUEST_TIMEOUT_MS = 8000;
  const MAX_VIEWED = 12;
  const sessionLists = { [WISHLIST_KEY]: [], [VIEWED_KEY]: [] };
  const persistentLists = { [WISHLIST_KEY]: null, [VIEWED_KEY]: null };
  const sessionWishlistHandles = Object.create(null);
  let persistentWishlistHandles = null;
  let preferencesState = 'unknown';
  let preferencesReady = false;
  let privacyUiReady = false;
  let recentlyViewedNodes = [];
  let recentlyViewedRenderVersion = 0;
  let wishlistRenderVersion = 0;
  let wishlistDialogState = null;
  let wishlistReturnFocus = null;

  function storage() {
    try { return localStorage; } catch(e) { return null; }
  }

  function parseList(value) {
    try {
      const parsed = JSON.parse(value || '[]');
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch(e) { return []; }
  }

  function safeProductHandle(value) {
    const handle = String(value || '');
    return /^[a-z0-9][a-z0-9-]*$/.test(handle) ? handle : '';
  }

  function parseWishlistHandles(value) {
    const result = Object.create(null);
    try {
      const parsed = JSON.parse(value || '{}');
      if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') return result;
      Object.entries(parsed).forEach(([id, value]) => {
        const handle = safeProductHandle(value);
        if (id && handle) result[String(id)] = handle;
      });
    } catch(e) {}
    return result;
  }

  function persisted(key) {
    if (refreshPreferences(false) !== 'allowed') return [];
    if (persistentLists[key] !== null) return persistentLists[key];
    const target = storage();
    try { persistentLists[key] = parseList(target && target.getItem(key)); }
    catch(e) { persistentLists[key] = []; }
    return persistentLists[key];
  }

  function persistedWishlistHandleMap() {
    if (refreshPreferences(false) !== 'allowed') return Object.create(null);
    if (persistentWishlistHandles !== null) return persistentWishlistHandles;
    const target = storage();
    try { persistentWishlistHandles = parseWishlistHandles(target && target.getItem(WISHLIST_HANDLES_KEY)); }
    catch(e) { persistentWishlistHandles = Object.create(null); }
    return persistentWishlistHandles;
  }

  function load(key) {
    const combined = sessionLists[key].concat(persisted(key));
    return combined.filter((value, index) => combined.indexOf(value) === index);
  }

  function loadWishlistHandles() {
    return Object.assign(Object.create(null), persistedWishlistHandleMap(), sessionWishlistHandles);
  }

  function writePersistentList(key) {
    if (refreshPreferences(false) !== 'allowed') return;
    const target = storage();
    try { if (target) target.setItem(key, JSON.stringify(persistentLists[key] || [])); } catch(e){}
  }

  function writePersistentWishlistHandles() {
    if (refreshPreferences(false) !== 'allowed') return;
    const target = storage();
    try { if (target) target.setItem(WISHLIST_HANDLES_KEY, JSON.stringify(persistentWishlistHandles || {})); } catch(e){}
  }

  function setListItem(key, value, present, prepend) {
    const item = String(value);
    sessionLists[key] = sessionLists[key].filter((entry) => entry !== item);
    if (present) prepend ? sessionLists[key].unshift(item) : sessionLists[key].push(item);
    if (key === VIEWED_KEY && sessionLists[key].length > MAX_VIEWED) sessionLists[key].length = MAX_VIEWED;

    if (refreshPreferences(false) !== 'allowed') return;
    const saved = persisted(key).filter((entry) => entry !== item);
    if (present) prepend ? saved.unshift(item) : saved.push(item);
    persistentLists[key] = key === VIEWED_KEY ? saved.slice(0, MAX_VIEWED) : saved;
    writePersistentList(key);
  }

  function setWishlistItem(productId, handle, present) {
    const id = String(productId);
    const current = load(WISHLIST_KEY);
    if (present && !current.includes(id) && current.length >= MAX_NEW_WISHLIST) {
      setWishlistStatus(`Você atingiu o limite de ${MAX_NEW_WISHLIST} novos favoritos neste dispositivo.`);
      return false;
    }

    setListItem(WISHLIST_KEY, id, present, false);
    if (present && handle) sessionWishlistHandles[id] = handle;
    else delete sessionWishlistHandles[id];

    if (refreshPreferences(false) === 'allowed') {
      const savedHandles = Object.assign(Object.create(null), persistedWishlistHandleMap());
      if (present && handle) savedHandles[id] = handle;
      else delete savedHandles[id];
      persistentWishlistHandles = savedHandles;
      writePersistentWishlistHandles();
    }
    syncWishlistUi();
    return true;
  }

  function clearPersistentLists() {
    const target = storage();
    [WISHLIST_KEY, WISHLIST_HANDLES_KEY, VIEWED_KEY].forEach((key) => {
      try { if (target) target.removeItem(key); } catch(e){}
    });
    persistentLists[WISHLIST_KEY] = null;
    persistentLists[VIEWED_KEY] = null;
    persistentWishlistHandles = null;
  }

  function setHeartState(button, active) {
    if (button.classList.toggle) button.classList.toggle('is-active', active);
    else if (active) button.classList.add('is-active');
    else button.classList.remove('is-active');
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    button.setAttribute('aria-label', active ? 'Remover dos favoritos' : 'Adicionar aos favoritos');
  }

  function syncHeartButtons() {
    const wishlist = load(WISHLIST_KEY);
    document.querySelectorAll('.mak-wishlist-btn').forEach((button) => {
      setHeartState(button, wishlist.includes(String(button.dataset.productId || '')));
    });
  }

  function syncWishlistTrigger() {
    const count = load(WISHLIST_KEY).length;
    document.querySelectorAll('[data-mak-wishlist-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-label', count === 0 ? 'Abrir favoritos' : `Abrir favoritos, ${count} ${count === 1 ? 'item' : 'itens'}`);
      const countNode = trigger.querySelector('[data-mak-wishlist-count]');
      if (countNode) {
        if (countNode.textContent !== String(count)) countNode.textContent = String(count);
        countNode.hidden = count === 0;
      }
    });
  }

  function syncWishlistUi() {
    syncHeartButtons();
    syncWishlistTrigger();
  }

  function cancelWishlistRequests(state) {
    if (!state || !state.requests) return;
    [...state.requests].forEach((request) => request.cancel());
  }

  function fetchWishlistProduct(state, url) {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let timerId = null;
    let settled = false;
    let rejectRequest;
    const request = {
      cancel() {
        if (controller) controller.abort();
        const error = new Error('Wishlist request cancelled');
        error.name = 'AbortError';
        if (rejectRequest && !settled) {
          settled = true;
          if (typeof clearTimeout === 'function' && timerId !== null) clearTimeout(timerId);
          rejectRequest(error);
        }
      },
    };
    const promise = new Promise((resolve, reject) => {
      rejectRequest = reject;
      const finish = (callback, value) => {
        if (settled) return;
        settled = true;
        if (typeof clearTimeout === 'function' && timerId !== null) clearTimeout(timerId);
        callback(value);
      };
      timerId = setTimeout(() => {
        if (controller) controller.abort();
        const error = new Error('Wishlist request timed out');
        error.name = 'TimeoutError';
        finish(reject, error);
      }, WISHLIST_REQUEST_TIMEOUT_MS);
      Promise.resolve()
        .then(() => fetch(url, controller ? { signal: controller.signal } : undefined))
        .then((response) => {
          if (!response.ok || typeof response.json !== 'function') {
            finish(resolve, { response, product: null });
            return;
          }
          Promise.resolve(response.json()).then(
            (product) => finish(resolve, { response, product }),
            (error) => finish(reject, error)
          );
        }, (error) => finish(reject, error));
    });
    state.requests.add(request);
    return promise.finally(() => state.requests.delete(request));
  }

  function refreshPreferences(confirmed) {
    const previous = preferencesState;
    if (confirmed) preferencesReady = true;
    if (!preferencesReady) {
      preferencesState = 'unknown';
      return preferencesState;
    }
    let next = 'unknown';
    try {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (privacy && typeof privacy.preferencesProcessingAllowed === 'function') {
        const allowed = privacy.preferencesProcessingAllowed();
        if (allowed === true) next = 'allowed';
        else if (allowed === false) next = 'denied';
      }
    } catch(e) { next = 'unknown'; }
    preferencesState = next;
    if (next === 'denied' && previous !== 'denied') {
      clearPersistentLists();
      wishlistRenderVersion += 1;
    }
    return preferencesState;
  }

  function refreshPrivacyUi(previous) {
    syncWishlistUi();
    if (preferencesState === 'allowed') enrichObservedWishlistHandles();
    if (preferencesState === 'denied' && previous !== 'denied') closeAndClearWishlistDialog();
    else if (wishlistDialogState && wishlistDialogState.dialog.open && previous !== preferencesState) renderWishlistDialog();
    if (privacyUiReady && previous !== preferencesState) renderRecentlyViewed();
  }

  function initPreferences() {
    document.addEventListener('visitorConsentCollected', () => {
      const previous = preferencesState;
      refreshPreferences(true);
      refreshPrivacyUi(previous);
    });
    try {
      const shopify = window.Shopify;
      if (!shopify || typeof shopify.loadFeatures !== 'function') return;
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        const previous = preferencesState;
        if (error) {
          preferencesReady = false;
          preferencesState = 'unknown';
          refreshPrivacyUi(previous);
          return;
        }
        refreshPreferences(true);
        refreshPrivacyUi(previous);
      });
    } catch(e) { preferencesReady = false; preferencesState = 'unknown'; }
  }

  function safeProductImageUrl(value) {
    if (typeof value !== 'string' || !value.trim()) return '';
    try {
      const url = new URL(value, window.location.origin);
      const host = url.hostname.toLowerCase();
      const isShopifyCdn = host === 'cdn.shopify.com' || host === 'shopifycdn.com' || host.endsWith('.shopifycdn.com');
      if (url.protocol !== 'https:' || (url.origin !== window.location.origin && !isShopifyCdn)) return '';
      return url.href;
    } catch(e) { return ''; }
  }

  function formatPrice(value) {
    if (typeof value !== 'number' || !Number.isFinite(value) || !Number.isSafeInteger(value) || value < 0) return '';
    const [whole, fraction] = (value / 100).toFixed(2).split('.');
    return `R$ ${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${fraction}`;
  }

  function buildRecentlyViewedCard(product) {
    const handle = safeProductHandle(product && product.handle);
    if (!handle) return null;

    const card = document.createElement('a');
    card.href = `/products/${handle}`;
    card.className = 'mak-recently-viewed__card';
    const imageUrl = safeProductImageUrl(product.featured_image || (product.images && product.images[0]));
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = String(product.title || '');
      image.loading = 'lazy';
      card.appendChild(image);
    }
    const info = document.createElement('div');
    info.className = 'mak-recently-viewed__card-info';
    const title = document.createElement('div');
    title.className = 'mak-recently-viewed__card-title';
    title.textContent = String(product.title || '');
    info.appendChild(title);
    const formattedPrice = formatPrice(product.price);
    if (formattedPrice) {
      const price = document.createElement('div');
      price.className = 'mak-recently-viewed__card-price';
      price.textContent = formattedPrice;
      info.appendChild(price);
    }
    card.appendChild(info);
    return card;
  }

  function observedProductHandle(card) {
    const link = card.querySelector('a[href]');
    if (!link) return '';
    try {
      const url = new URL(link.href || link.getAttribute('href'), window.location.origin);
      if (url.origin !== window.location.origin) return '';
      const match = url.pathname.match(/^\/products\/([a-z0-9][a-z0-9-]*)$/);
      return match ? safeProductHandle(match[1]) : '';
    } catch(e) { return ''; }
  }

  function enrichPersistedWishlistHandle(productId, handle) {
    if (!handle || refreshPreferences(false) !== 'allowed') return;
    const id = String(productId);
    if (!persisted(WISHLIST_KEY).includes(id)) return;
    const savedHandles = Object.assign(Object.create(null), persistedWishlistHandleMap());
    if (savedHandles[id] === handle) return;
    savedHandles[id] = handle;
    persistentWishlistHandles = savedHandles;
    writePersistentWishlistHandles();
  }

  function enrichObservedWishlistHandles() {
    document.querySelectorAll('.mak-wishlist-btn').forEach((button) => {
      enrichPersistedWishlistHandle(button.dataset.productId, safeProductHandle(button.dataset.productHandle));
    });
  }

  function addHeartButtons() {
    const wishlist = load(WISHLIST_KEY);
    const cards = [
      ...document.querySelectorAll('product-card:not([data-mak-heart])'),
      ...document.querySelectorAll('.mv2-card:not([data-mak-heart])'),
    ];
    cards.forEach(card => {
      card.dataset.makHeart = '1';
      const productId = String(card.dataset.productId || '');
      if (!productId) return;
      const isMv2Card = card.classList && card.classList.contains('mv2-card');
      const gallery = isMv2Card ? card : card.querySelector('.card-gallery, .media-fit');
      if (!gallery) return;
      const handle = isMv2Card ? safeProductHandle(card.dataset.handle) : observedProductHandle(card);
      if (wishlist.includes(productId)) enrichPersistedWishlistHandle(productId, handle);

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'mak-wishlist-btn';
      btn.dataset.productId = productId;
      if (handle) btn.dataset.productHandle = handle;
      if (isMv2Card) {
        btn.classList.add('mak-wishlist-btn--mv2');
        btn.style.top = '48px';
        btn.style.right = '10px';
      }
      setHeartState(btn, wishlist.includes(productId));
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        const active = load(WISHLIST_KEY).includes(productId);
        if (!active && load(WISHLIST_KEY).length >= MAX_NEW_WISHLIST) {
          setWishlistStatus(`Você atingiu o limite de ${MAX_NEW_WISHLIST} novos favoritos neste dispositivo.`);
          syncWishlistUi();
          return;
        }
        if (!active) {
          btn.style.transform = 'scale(1.3)';
          setTimeout(() => btn.style.transform = '', 200);
        }
        document.querySelectorAll('[data-mak-wishlist-feedback]').forEach((node) => { node.textContent = ''; });
        setWishlistItem(productId, handle, !active);
        window.dispatchEvent(new CustomEvent('mak-wishlist-updated'));
        if (wishlistDialogState && wishlistDialogState.dialog.open) renderWishlistDialog();
      });
      gallery.appendChild(btn);
    });
    syncWishlistUi();
  }

  function trackViewed() {
    if (window.location.pathname.indexOf('/products/') !== 0) return;
    const slug = window.location.pathname.replace('/products/', '').split('?')[0].split('/')[0];
    if (!slug) return;
    setListItem(VIEWED_KEY, slug, true, true);
  }

  async function renderRecentlyViewed() {
    const renderVersion = ++recentlyViewedRenderVersion;
    const container = document.querySelector('[data-mak-recently-viewed]');
    if (!container) return;
    recentlyViewedNodes.forEach((node) => { if (node && typeof node.remove === 'function') node.remove(); });
    recentlyViewedNodes = [];
    const viewed = load(VIEWED_KEY);
    const currentSlug = window.location.pathname.startsWith('/products/')
      ? window.location.pathname.replace('/products/', '').split('?')[0]
      : null;
    const list = viewed.filter(s => s !== currentSlug).map(safeProductHandle).filter(Boolean).slice(0, 8);
    if (list.length === 0) return;

    const frag = document.createDocumentFragment();
    const grid = document.createElement('div');
    grid.className = 'mak-recently-viewed__grid';
    for (const slug of list) {
      try {
        const res = await fetch(`/products/${slug}.js`);
        if (renderVersion !== recentlyViewedRenderVersion) return;
        if (!res.ok) continue;
        const p = await res.json();
        if (renderVersion !== recentlyViewedRenderVersion) return;
        const card = buildRecentlyViewedCard(p);
        if (card) grid.appendChild(card);
      } catch(e) {}
    }
    if (renderVersion === recentlyViewedRenderVersion && grid.children.length > 0) {
      const title = document.createElement('h3');
      title.className = 'mak-recently-viewed__title';
      title.textContent = 'Produtos vistos recentemente';
      frag.appendChild(title);
      frag.appendChild(grid);
      container.appendChild(frag);
      recentlyViewedNodes = [title, grid];
    }
  }

  function setWishlistStatus(message) {
    const status = document.querySelector('[data-mak-wishlist-status]');
    if (status) status.textContent = message;
    document.querySelectorAll('[data-mak-wishlist-feedback]').forEach((node) => { node.textContent = message; });
  }

  function wishlistDialogElements() {
    const dialog = document.querySelector('[data-mak-wishlist-dialog]');
    if (!dialog) return null;
    return {
      dialog,
      list: dialog.querySelector('[data-mak-wishlist-list]'),
      more: dialog.querySelector('[data-mak-wishlist-more]'),
      status: dialog.querySelector('[data-mak-wishlist-status]'),
    };
  }

  function closeAndClearWishlistDialog() {
    cancelWishlistRequests(wishlistDialogState);
    wishlistRenderVersion += 1;
    const elements = wishlistDialogElements();
    if (!elements) return;
    if (elements.list) elements.list.replaceChildren();
    if (elements.status) elements.status.textContent = '';
    if (elements.more) elements.more.hidden = true;
    if (elements.dialog.open && typeof elements.dialog.close === 'function') elements.dialog.close();
  }

  function entryCanRender(entry) {
    return entry.inMemory || refreshPreferences(false) === 'allowed';
  }

  function buildUnavailableWishlistItem(id, messageText) {
    const item = document.createElement('article');
    item.className = 'mak-wishlist-dialog__unavailable';
    const message = document.createElement('p');
    message.textContent = messageText || 'Favorito indisponível neste dispositivo. Abra novamente o produto no catálogo para recuperá-lo.';
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.dataset.makWishlistRemove = id;
    remove.textContent = 'Remover dos favoritos';
    remove.addEventListener('click', () => removeWishlistItem(id));
    item.append(message, remove);
    return item;
  }

  function buildWishlistItem(product, id, expectedHandle) {
    const handle = safeProductHandle(product && product.handle);
    if (!handle || handle !== expectedHandle || String(product.id) !== String(id)) return null;
    const item = document.createElement('article');
    item.className = 'mak-wishlist-dialog__item';
    const imageUrl = safeProductImageUrl(product.featured_image || (product.images && product.images[0]));
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = String(product.title || '');
      image.loading = 'lazy';
      item.appendChild(image);
    }
    const content = document.createElement('div');
    content.className = 'mak-wishlist-dialog__content';
    const title = document.createElement('h3');
    title.className = 'mak-wishlist-dialog__title';
    title.textContent = String(product.title || 'Produto favorito');
    content.appendChild(title);
    const formattedPrice = formatPrice(product.price);
    if (formattedPrice) {
      const price = document.createElement('p');
      price.className = 'mak-wishlist-dialog__price';
      price.textContent = formattedPrice;
      content.appendChild(price);
    }
    const actions = document.createElement('div');
    actions.className = 'mak-wishlist-dialog__actions';
    const cta = document.createElement('a');
    cta.className = 'mak-wishlist-dialog__cta';
    cta.href = `/products/${handle}`;
    cta.textContent = 'Ver opções e comprar';
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.dataset.makWishlistRemove = id;
    remove.textContent = 'Remover dos favoritos';
    remove.addEventListener('click', () => removeWishlistItem(id));
    actions.append(cta, remove);
    content.appendChild(actions);
    item.appendChild(content);
    return item;
  }

  function removeWishlistItem(id) {
    setWishlistItem(id, '', false);
    window.dispatchEvent(new CustomEvent('mak-wishlist-updated'));
    renderWishlistDialog();
  }

  async function renderNextWishlistPage() {
    const state = wishlistDialogState;
    if (!state || state.renderVersion !== wishlistRenderVersion) return;
    const end = Math.min(state.nextIndex + WISHLIST_PAGE_SIZE, state.entries.length);
    state.more.hidden = true;
    let failures = 0;
    for (; state.nextIndex < end; state.nextIndex += 1) {
      const entry = state.entries[state.nextIndex];
      if (state.renderVersion !== wishlistRenderVersion) return;
      if (!entry.handle) {
        state.list.appendChild(buildUnavailableWishlistItem(entry.id));
        continue;
      }
      if (!entryCanRender(entry)) {
        failures += 1;
        continue;
      }
      try {
        const result = await fetchWishlistProduct(state, `/products/${entry.handle}.js`);
        if (state.renderVersion !== wishlistRenderVersion) return;
        if (!entryCanRender(entry)) {
          state.status.textContent = 'Não foi possível carregar favoritos enquanto as preferências estão indisponíveis.';
          return;
        }
        const response = result.response;
        if (!response.ok) {
          failures += 1;
          state.list.appendChild(buildUnavailableWishlistItem(entry.id, 'Este favorito não pôde ser carregado. Você pode removê-lo da lista.'));
          continue;
        }
        const product = result.product;
        if (state.renderVersion !== wishlistRenderVersion) return;
        if (!entryCanRender(entry)) {
          state.status.textContent = 'Não foi possível carregar favoritos enquanto as preferências estão indisponíveis.';
          return;
        }
        const item = buildWishlistItem(product, entry.id, entry.handle);
        if (item) state.list.appendChild(item);
        else {
          failures += 1;
          state.list.appendChild(buildUnavailableWishlistItem(entry.id, 'Este favorito não pôde ser confirmado. Você pode removê-lo da lista.'));
        }
      } catch(e) {
        if (state.renderVersion !== wishlistRenderVersion) return;
        failures += 1;
        state.list.appendChild(buildUnavailableWishlistItem(entry.id, 'Este favorito não pôde ser carregado. Você pode removê-lo da lista.'));
      }
    }
    if (state.renderVersion !== wishlistRenderVersion) return;
    if (failures > 0) state.status.textContent = `Não foi possível carregar ${failures} ${failures === 1 ? 'favorito' : 'favoritos'}. Feche e abra a lista para tentar novamente.`;
    else state.status.textContent = `${state.list.children.length} ${state.list.children.length === 1 ? 'favorito exibido' : 'favoritos exibidos'}.`;
    state.more.hidden = state.nextIndex >= state.entries.length;
  }

  function renderWishlistDialog() {
    const elements = wishlistDialogElements();
    if (!elements || !elements.list || !elements.more || !elements.status) return;
    cancelWishlistRequests(wishlistDialogState);
    const renderVersion = ++wishlistRenderVersion;
    elements.list.replaceChildren();
    elements.more.hidden = true;
    const ids = load(WISHLIST_KEY);
    const handles = loadWishlistHandles();
    if (ids.length === 0) {
      elements.status.textContent = 'Sua lista de favoritos está vazia.';
      wishlistDialogState = { ...elements, entries: [], nextIndex: 0, renderVersion };
      return;
    }
    const entries = ids.map((id) => ({
      id,
      handle: handles[id] || '',
      inMemory: sessionLists[WISHLIST_KEY].includes(id) && Boolean(sessionWishlistHandles[id]),
    }));
    elements.status.textContent = 'Carregando favoritos…';
    wishlistDialogState = { ...elements, entries, nextIndex: 0, renderVersion, requests: new Set() };
    renderNextWishlistPage();
  }

  function initWishlistDialog() {
    const elements = wishlistDialogElements();
    if (!elements) return;
    document.querySelectorAll('[data-mak-wishlist-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        document.querySelectorAll('[data-mak-wishlist-feedback]').forEach((node) => { node.textContent = ''; });
        wishlistReturnFocus = trigger;
        if (!elements.dialog.open && typeof elements.dialog.showModal === 'function') elements.dialog.showModal();
        renderWishlistDialog();
      });
    });
    const close = elements.dialog.querySelector('[data-mak-wishlist-close]');
    if (close) close.addEventListener('click', () => elements.dialog.close());
    elements.dialog.addEventListener('close', () => {
      cancelWishlistRequests(wishlistDialogState);
      wishlistRenderVersion += 1;
      if (wishlistReturnFocus && typeof wishlistReturnFocus.focus === 'function') wishlistReturnFocus.focus();
      wishlistReturnFocus = null;
    });
    elements.more.addEventListener('click', () => renderNextWishlistPage());
  }

  function init() {
    trackViewed();
    initWishlistDialog();
    addHeartButtons();
    renderRecentlyViewed();
    const observer = new MutationObserver(() => addHeartButtons());
    observer.observe(document.body, { childList: true, subtree: true });
    privacyUiReady = true;
  }

  initPreferences();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
